import React, {useEffect, useRef} from 'react';
import {useRouteMatch} from 'react-router';
import {useParams, useLocation} from 'react-router-dom';
import {useQuery} from '@apollo/client';

import {renderModalError} from '@utils/forms';
import {GetDurationAndMatch, ReportState, GetReports} from '@graphql/reports/query';
import {routes} from '@constants/routes';
import {getLink} from '@appComponents/Link';
import {isUrlParamValid, convertToInt} from '@utils/helpers';
import {showModal} from '@utils/modal';
import {EventsInvoiceStatus} from '@modules/events/forms/EventsInvoiceStatus';
import {useModalRouting} from '@utils/hooks/useModalRouting';
import {Reports} from '@modules/events/views/Reports';
import {useEventsReportRoutes} from '@modules/events/utils/eventsReport';

export default function EventsReport() {
    const {id, reportId} = useParams();
    const location = useLocation();
    const match = useRouteMatch();
    const eventId = convertToInt(id);
    const reportIndexUrl = getLink('events.report.index', {id: eventId});
    const prevPathname = useRef(location.pathname);

    const {data: reportStateData, loading: reportStateLoading, refetch: refetchReportState} = useQuery(ReportState, {
        variables: {event: eventId},
        fetchPolicy: 'cache-and-network',
    });

    const {data: reportsData, loading: reportsLoading, refetch: refetchReports} = useQuery(GetReports, {
        variables: {event: eventId},
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
    });

    const {data: durationAndMatchData, loading: loadingDurationAndMatch} = useQuery(GetDurationAndMatch, {
        variables: {id: eventId},
    });

    const isReportable = reportStateData?.reportState?.isReportable ?? true;
    const isLoading = reportStateLoading || reportsLoading || loadingDurationAndMatch;
    const isBlockingModalRouting =
        (reportStateLoading && !reportStateData)
        || (reportsLoading && !reportsData)
        || (loadingDurationAndMatch && !durationAndMatchData);

    useEffect(() => {
        if (!isUrlParamValid(id)) {
            return showModal({
                isVisible: true,
                content: renderModalError('Event', routes.events.index.path),
            });
        }

        if (reportId && !isUrlParamValid(reportId)
            && !match.url.includes('add-working') && !match.url.includes('add-not-streamed')
            && !match.url.includes('invoice-status')) {
            return showModal({
                isVisible: true,
                content: renderModalError('Report', getLink('events.report.index', {id})),
            });
        }
    }, [id, reportId, match.url]);

    useEffect(() => {
        if (location.pathname === reportIndexUrl && prevPathname.current !== location.pathname) {
            refetchReportState();
            refetchReports();
        }
        prevPathname.current = location.pathname;
    }, [location.pathname, reportIndexUrl, refetchReportState, refetchReports]);

    useModalRouting({
        routesMap: useEventsReportRoutes(reportStateData, reportsData, durationAndMatchData),
        shouldHandleModal: !isBlockingModalRouting,
        indexUrl: reportIndexUrl,
    });

    if (!isReportable) {
        return <div>The report is not available because the event has no event contents with distributions.</div>;
    }

    return (
        <div className='eventReport'>
            <Reports
                eventId={eventId}
                buttonData={reportStateData?.reportState}
                loading={isLoading}
                reportsData={reportsData?.reports || []}
                duration={durationAndMatchData?.event?.duration}
                match={durationAndMatchData?.event?.sr_event?.match}
            />
            <EventsInvoiceStatus eventId={eventId}/>
        </div>
    );
}
